/**
 * ReleasedHoldsBanner — paquetes que estaban APARTADOS por retención de ML
 * (verificación de documentación / cupo del comprador), ya escaneados
 * (están físicamente en el depósito) y que ML LIBERÓ: listos para que el
 * operador los saque del estante, re-escanee y procese normal.
 * La fila desaparece sola al procesarse (lazy-resolve del backend).
 */
import { useEffect, useState } from 'react';
import { api } from '../../api/client';

interface ReleasedCase {
  id: number;
  ml_order_id: string;
  asin: string | null;
  title: string | null;
  thumbnail: string | null;
  quantity: number | null;
  arrived_at_wh: string;
  detected_at: string;
}

function ageText(iso: string): string {
  const hours = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  if (hours < 1) return 'recién';
  if (hours < 24) return `hace ${hours}h`;
  return `hace ${Math.floor(hours / 24)}d`;
}

export function ReleasedHoldsBanner({ sellerId }: { sellerId: number }) {
  const [items, setItems] = useState<ReleasedCase[]>([]);
  const [expanded, setExpanded] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get<{ cases: ReleasedCase[] }>(
        `/amazon/receiver-validation/cases?seller_id=${sellerId}&wh_ready=true`
      );
      if (r.success && r.data) setItems(r.data.cases || []);
    } catch {
      // Silent — keep last known list
    }
  };

  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerId]);

  if (items.length === 0) return null;

  return (
    <div className="bg-green-50 border-l-4 border-green-600 p-4 mb-4 rounded">
      <div className="flex justify-between items-center">
        <strong>
          ✅ {items.length}{' '}
          {items.length === 1
            ? 'paquete apartado fue liberado por ML'
            : 'paquetes apartados fueron liberados por ML'}{' '}
          — sacar del estante y procesar
        </strong>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-sm bg-white border px-3 py-1 rounded hover:bg-gray-50"
        >
          {expanded ? 'Ocultar' : 'Ver lista'}
        </button>
      </div>

      {expanded && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-green-100">
              <tr>
                <th className="text-left p-2">Producto</th>
                <th className="text-left p-2">Orden</th>
                <th className="text-left p-2">Cant.</th>
                <th className="text-left p-2">Llegó al depósito</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-green-100">
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      {it.thumbnail && (
                        <img src={it.thumbnail} alt="" className="w-8 h-8 rounded object-cover" />
                      )}
                      <span className="max-w-[360px] truncate">
                        {it.title || it.asin || it.ml_order_id}
                      </span>
                    </div>
                  </td>
                  <td className="p-2 font-mono text-xs">#{it.ml_order_id}</td>
                  <td className="p-2">{it.quantity ?? 1}</td>
                  <td className="p-2">{ageText(it.arrived_at_wh)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
